"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Wallet,
  AlertTriangle,
  Download,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { useToast } from "@/components/Toast/ToastContext";
import {
  WALLET_CONNECT_ATTENTION_EVENT,
  WALLET_CONNECT_ATTENTION_CLEAR_EVENT,
  WALLET_OPEN_CONNECT_MENU_EVENT,
} from "@/utils/walletConnectAttention";
import {
  canOpenWalletInAppBrowser,
  openWalletInAppBrowser,
} from "@/utils/mobileWalletBrowse";


const WALLET_ATTENTION_MS = 45000;
const WALLET_DROPDOWN_WIDTH_PX = 320;
const WALLET_DROPDOWN_Z_INDEX = 170;
/** Tailwind `lg`; below this we use `@solana/wallet-adapter-react-ui` modal (mobile / tablet). */
const LG_MIN_PX = 1024;

export default function WalletConnectButton({
  showWalletDropdown,
  setShowWalletDropdown,
  isConnecting,
  walletError,
  clearError,
  walletProviders,
  handleWalletConnection,
  getWalletStatus,
  /** `"anchor"` (default Tools header) or `"center"` (e.g. AI Coin Lab chat). */
  menuPlacement = "anchor",
  /** When `menuPlacement="center"`, center inside this element (not the full viewport). */
  menuCenterSelector = null,
  /** Hide Connect header + Protected-by footer (AI Coin Lab). */
  compactMenu = false,
}) {
  const { dismissAllToasts } = useToast();
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const dropdownContainerRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  /** Increments on each attention signal so the auto-hide timer resets. */
  const [attentionEpoch, setAttentionEpoch] = useState(0);
  const [showOtherWallets, setShowOtherWallets] = useState(false);

  useEffect(() => {
    if (!showWalletDropdown) setShowOtherWallets(false);
  }, [showWalletDropdown]);

  useEffect(() => {
    const onAttention = () => setAttentionEpoch((e) => e + 1);
    const onClear = () => setAttentionEpoch(0);
    /** Fallback when `dispatchWalletOpenConnectMenu` cannot find the CTA (e.g. rare timing). */
    const onOpenMenu = () => {
      dismissAllToasts();
      setAttentionEpoch(0);
      const isDesktopWide =
        typeof window !== "undefined" &&
        window.matchMedia(`(min-width: ${LG_MIN_PX}px)`).matches;
      if (isDesktopWide) {
        setShowWalletDropdown(true);
      } else {
        setShowWalletDropdown(false);
        setWalletModalVisible(true);
      }
    };
    window.addEventListener(WALLET_CONNECT_ATTENTION_EVENT, onAttention);
    window.addEventListener(WALLET_CONNECT_ATTENTION_CLEAR_EVENT, onClear);
    window.addEventListener(WALLET_OPEN_CONNECT_MENU_EVENT, onOpenMenu);
    return () => {
      window.removeEventListener(WALLET_CONNECT_ATTENTION_EVENT, onAttention);
      window.removeEventListener(WALLET_CONNECT_ATTENTION_CLEAR_EVENT, onClear);
      window.removeEventListener(WALLET_OPEN_CONNECT_MENU_EVENT, onOpenMenu);
    };
  }, [
    dismissAllToasts,
    setShowWalletDropdown,
    setWalletModalVisible,
  ]);

  useEffect(() => {
    if (attentionEpoch === 0) return undefined;
    const id = window.setTimeout(
      () => setAttentionEpoch(0),
      WALLET_ATTENTION_MS,
    );
    return () => window.clearTimeout(id);
  }, [attentionEpoch]);

  useEffect(() => {
    if (!showWalletDropdown) {
      setDropdownPosition(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      if (menuPlacement === "center") {
        const host =
          typeof menuCenterSelector === "string" && menuCenterSelector
            ? document.querySelector(menuCenterSelector)
            : null;
        const bounds = host?.getBoundingClientRect();
        if (bounds && bounds.width > 0 && bounds.height > 0) {
          const width = Math.min(WALLET_DROPDOWN_WIDTH_PX, bounds.width - 32);
          setDropdownPosition({
            top: bounds.top + bounds.height / 2,
            left: bounds.left + bounds.width / 2,
            width: Math.max(240, width),
            transform: "translate(-50%, -50%)",
            maxHeight: Math.min(bounds.height - 48, 560),
            backdrop: {
              top: bounds.top,
              left: bounds.left,
              width: bounds.width,
              height: bounds.height,
            },
          });
          return;
        }
        const width = Math.min(WALLET_DROPDOWN_WIDTH_PX, window.innerWidth - 32);
        setDropdownPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
          width,
          transform: "translate(-50%, -50%)",
          maxHeight: Math.min(window.innerHeight - 48, 560),
          backdrop: null,
        });
        return;
      }
      const anchor = dropdownContainerRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(WALLET_DROPDOWN_WIDTH_PX, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      setDropdownPosition({
        top: rect.bottom + 4,
        left,
        width,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [showWalletDropdown, menuPlacement, menuCenterSelector]);

  const toggleDropdown = useCallback((event) => {
    // Prevent document outside-click from treating this gesture as "outside" (detached target bug).
    event?.stopPropagation?.();

    const wasOpen = showWalletDropdown;
    if (!wasOpen) {
      // Clear attention animation only when opening the menu — not when closing.
      setAttentionEpoch(0);
      // Defer toast provider updates to avoid cross-component state updates in event stack.
      queueMicrotask(() => {
        dismissAllToasts();
      });
    }

    setShowWalletDropdown(!wasOpen);
  }, [showWalletDropdown, setShowWalletDropdown, dismissAllToasts]);

  const showMotionRing = attentionEpoch > 0 && !isConnecting;

  const connectButtonClass = showMotionRing
    ? "wallet-connect-cta-btn flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-gray-700 shadow-inner shadow-black/20 transition-colors hover:bg-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
    : "wallet-connect-cta-btn flex items-center gap-1 rounded-lg border border-gray-600 bg-gray-700 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm";

  const walletErrorBanner = walletError ? (
    <div
      className={`p-3 bg-red-500/20 border border-red-500/30 rounded-lg ${
        compactMenu ? "m-3" : "mt-2"
      }`}
    >
      <div className="flex items-start space-x-2">
        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-red-400 text-sm font-medium">
            Connection Error
          </div>
          <div className="text-red-300 text-xs mt-1">{walletError}</div>
        </div>
      </div>
      <button
        onClick={clearError}
        className="mt-2 text-xs text-red-300 hover:text-red-200 underline"
      >
        Dismiss
      </button>
    </div>
  ) : null;

  const renderProviderRow = (provider) => {
    const status = getWalletStatus(provider);
    const StatusIcon = status.icon;
    const openInWalletApp =
      !provider.installed &&
      !provider.disabled &&
      canOpenWalletInAppBrowser(provider.id);
    const openThisWallet = () => {
      if (provider.disabled || isConnecting) return;
      if (!provider.installed) {
        if (openInWalletApp) {
          openWalletInAppBrowser(provider.id);
          setShowWalletDropdown(false);
        }
        return;
      }
      handleWalletConnection(provider.id);
    };

    return (
      <div key={provider.id} className="relative">
        <div
          role="button"
          tabIndex={isConnecting || provider.disabled ? -1 : 0}
          aria-disabled={isConnecting || provider.disabled}
          onClick={openThisWallet}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === " ") &&
              !provider.disabled &&
              !isConnecting &&
              (provider.installed || openInWalletApp)
            ) {
              e.preventDefault();
              openThisWallet();
            }
          }}
          className={`w-full flex items-center justify-between p-4 hover:bg-gray-900 transition-colors text-sm border-b border-gray-800 last:border-b-0 ${
            provider.disabled ||
            isConnecting ||
            (!provider.installed && !openInWalletApp)
              ? "opacity-60 cursor-not-allowed"
              : "cursor-pointer"
          }`}
        >
          <div className="flex items-start gap-2 w-full">
            <div className="text-base shrink-0 mt-0.5">{provider.icon}</div>

            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-white font-medium text-sm truncate">
                  {provider.name}
                </span>
                {provider.recommended && (
                  <span className="shrink-0 bg-gray-700 text-gray-200 px-1.5 py-1 rounded text-[11px] leading-none">
                    Recommended
                  </span>
                )}
              </div>

              <div className="text-gray-400 text-start text-xs mt-0.5 leading-snug break-words">
                {provider.description}
              </div>

              {provider.disabled && (
                <div className="text-red-400 text-[11px] mt-0.5 font-medium">
                  Solana not supported
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {!provider.installed &&
            !provider.disabled &&
            (openInWalletApp || provider.downloadUrl) ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (openInWalletApp) {
                    openWalletInAppBrowser(provider.id);
                    setShowWalletDropdown(false);
                    return;
                  }
                  if (provider.downloadUrl) {
                    window.open(
                      provider.downloadUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }
                }}
                className="flex items-center space-x-1.5 px-2 py-1 text-gray-300 hover:text-white rounded-md transition-colors text-xs font-semibold hover:bg-gray-700/80"
              >
                {openInWalletApp ? (
                  <ExternalLink className="w-3.5 h-3.5" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span>{openInWalletApp ? "Open" : "Install"}</span>
              </button>
            ) : (
              <div className="text-right">
                <div className={`flex items-center space-x-1 ${status.color}`}>
                  <StatusIcon className="w-3 h-3" />
                  <span className="text-xs font-medium">{status.text}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const idxWalletProvider = walletProviders.find((p) => p.id === "idxwallet");
  const otherWalletProviders = walletProviders.filter(
    (p) => p.id !== "idxwallet",
  );

  const walletDropdownPanel = (
    <>
      {!compactMenu ? (
        <div className="p-4 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-200 text-sm font-medium">
              <Wallet className="w-3.5 h-3.5 mr-1.5" />
              Connect
            </div>
            <span className="text-xs text-gray-500">Solana Network</span>
          </div>
          {walletErrorBanner}
        </div>
      ) : (
        walletErrorBanner
      )}

      <div className="max-h-72 overflow-y-auto wallet-scrollbar bg-gray-950">
        {compactMenu ? (
          <>
            {idxWalletProvider ? renderProviderRow(idxWalletProvider) : null}
            {idxWalletProvider && otherWalletProviders.length > 0 ? (
              <button
                type="button"
                aria-expanded={showOtherWallets}
                onMouseDown={(e) => {
                  // Avoid document outside-click seeing a detached target after expand.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowOtherWallets((open) => !open);
                }}
                className="flex w-full items-center justify-between border-b border-gray-800 p-4 text-sm text-gray-300 transition-colors hover:bg-gray-900 hover:text-white"
              >
                <span className="font-medium">
                  {showOtherWallets ? "Less" : "Other"}
                </span>
                <ChevronDown
                  className={`h-4 w-4 opacity-70 transition-transform ${
                    showOtherWallets ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>
            ) : null}
            {showOtherWallets || !idxWalletProvider
              ? otherWalletProviders.map((provider) =>
                  renderProviderRow(provider),
                )
              : null}
          </>
        ) : (
          walletProviders.map((provider) => renderProviderRow(provider))
        )}
      </div>

      {!compactMenu ? (
        <div className="border-t border-gray-800 bg-gray-950 px-2 pt-3 pb-2">
          <div className="text-xs text-gray-400 text-center">
            Only Solana-compatible wallets can be connected
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <>
      <style jsx>{`
        .wallet-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .wallet-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
          border-radius: 4px;
        }
        .wallet-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.8);
          border-radius: 4px;
        }
        .wallet-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(113, 113, 122, 0.9);
        }
        .wallet-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(75, 85, 99, 0.8) rgba(31, 41, 55, 0.5);
        }

        @keyframes wallet-connect-border-walk {
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        /* Wrapper is visual only — clicks hit the button only (motion stays in background). */
        .wallet-connect-cta-shell {
          position: relative;
          display: inline-flex;
          border-radius: 0.5rem;
          vertical-align: middle;
          pointer-events: none;
        }

        .wallet-connect-cta-shell--motion {
          overflow: hidden;
        }

        .wallet-connect-border-spin {
          position: absolute;
          z-index: 0;
          left: 50%;
          top: 50%;
          width: 220%;
          height: 220%;
          pointer-events: none;
          transform: translate(-50%, -50%);
          background: conic-gradient(
            from 0deg,
            rgba(34, 197, 94, 0.15),
            rgba(52, 211, 153, 0.95),
            rgba(74, 222, 128, 1),
            rgb(34, 197, 94),
            rgba(22, 163, 74, 1),
            rgba(52, 211, 153, 0.95),
            rgba(34, 197, 94, 0.15)
          );
          animation: wallet-connect-border-walk 2.6s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .wallet-connect-border-spin {
            animation: none;
            background: linear-gradient(
              135deg,
              rgb(34, 197, 94),
              rgb(22, 163, 74)
            );
          }
        }

        .wallet-connect-cta-btn {
          position: relative;
          z-index: 1;
          pointer-events: auto;
          margin: 1px;
          border-radius: calc(0.5rem - 1px);
          outline: none;
        }

        .wallet-connect-cta-btn.wallet-connect-cta-btn--motion {
          border: none;
        }
      `}</style>
      <div className="relative dropdown-container" ref={dropdownContainerRef}>
        <div
          className={`wallet-connect-cta-shell ${showMotionRing ? "wallet-connect-cta-shell--motion" : ""}`}
        >
          {showMotionRing ? (
            <span className="wallet-connect-border-spin" aria-hidden />
          ) : null}
          <button
            type="button"
            data-idx-wallet-connect-cta
            onClick={toggleDropdown}
            className={`${connectButtonClass}${showMotionRing ? " wallet-connect-cta-btn--motion" : ""}`}
            disabled={isConnecting}
            aria-label={
              showMotionRing
                ? "Connect wallet — needed for recharge or refund"
                : "Connect wallet"
            }
            aria-expanded={showWalletDropdown}
            aria-haspopup="listbox"
          >
            <Wallet className="h-4 w-4" />
            <span className="max-[349px]:sr-only">
              {isConnecting ? "Connecting..." : "Connect"}
            </span>
          </button>
        </div>

        {showWalletDropdown &&
        dropdownPosition &&
        typeof document !== "undefined"
          ? createPortal(
              <>
                {menuPlacement === "center" ? (
                  <button
                    type="button"
                    aria-label="Close connect wallet"
                    className="fixed border-0 bg-black/55 p-0"
                    style={{
                      zIndex: WALLET_DROPDOWN_Z_INDEX - 1,
                      ...(dropdownPosition.backdrop
                        ? {
                            top: dropdownPosition.backdrop.top,
                            left: dropdownPosition.backdrop.left,
                            width: dropdownPosition.backdrop.width,
                            height: dropdownPosition.backdrop.height,
                          }
                        : { inset: 0 }),
                    }}
                    onClick={() => setShowWalletDropdown(false)}
                  />
                ) : null}
                <div
                  ref={dropdownPanelRef}
                  className="dropdown-container overflow-hidden rounded-lg border border-gray-700 bg-gray-950 shadow-2xl"
                  style={{
                    position: "fixed",
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    maxHeight: dropdownPosition.maxHeight,
                    transform: dropdownPosition.transform,
                    zIndex: WALLET_DROPDOWN_Z_INDEX,
                    display: menuPlacement === "center" ? "flex" : undefined,
                    flexDirection: menuPlacement === "center" ? "column" : undefined,
                  }}
                  role="listbox"
                  aria-label="Connect wallet options"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {walletDropdownPanel}
                </div>
              </>,
              document.body,
            )
          : null}
      </div>
    </>
  );
}
